import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * micromark 의 **indented code block** 구성만 끄는 remark 플러그인(self-plugin — transformer 없이
 * 파서 구성만 손댄다). CommonMark 는 4칸 이상 들여쓴 줄을 코드로 읽는데, 그 규칙이 필요 없는 문서
 * (들여쓰기가 의미 없는 서식으로 들어온 원문)에서 본문 전체가 코드블록으로 뒤집히는 걸 막는다.
 * fenced code(``` )는 그대로 살아 있다 — 끄는 건 들여쓰기 판정 하나뿐.
 *
 * `unified` 를 직접 의존하지 않으므로(react-markdown 의 transitive) `this` 는 쓰는 만큼만 좁혀 선언.
 */
function remarkDisableIndentedCode(this: unknown) {
  // `Data` 인터페이스엔 micromarkExtensions 선언이 없다(remark-parse 가 실행 시점에 채우는 슬롯).
  const processor = this as { data(): { micromarkExtensions?: unknown[] } };
  const data = processor.data();
  const extensions = (data.micromarkExtensions ??= []);
  extensions.push({ disable: { null: ['codeIndented'] } });
}

export interface MarkdownProps {
  /** 렌더할 마크다운 원문(CommonMark). */
  children: string;
  /**
   * 들여쓰기 코드블록(4칸 이상)을 코드로 읽을지. 기본 `true`(CommonMark 그대로).
   * 원문이 들여쓰기를 **서식 목적으로** 쓰는 문서(BE 약관 등)는 `false` — 안 그러면 본문이
   * 통째로 코드블록이 된다. fenced code(``` )는 이 값과 무관하게 항상 동작.
   */
  indentedCode?: boolean;
}

/**
 * 디자인시스템 토큰으로 렌더하는 범용 마크다운 뷰. `@tailwindcss/typography`(`prose`)가 태그별
 * 스타일을, `prose-heviton`(globals.css)이 색을 heviton 토큰에 매핑한다(다크모드 자동). 태그별
 * 컴포넌트 매핑 없음. CommonMark + GFM(`remark-gfm` — 표·취소선·task list·autolink). 도메인 무지 —
 * 약관·공지·도움말 등이 재사용.
 */
export function Markdown({ children, indentedCode = true }: MarkdownProps) {
  return (
    /* `[&_table]:block` — 넓은 표가 컨테이너를 밀어 **페이지 전체**가 가로 스크롤되는 걸 막는다
     * (모바일 WebView 치명). block 화된 table 이 자기 안에서만 가로 스크롤. 태그별 컴포넌트 매핑
     * (래퍼 엘리먼트 주입) 대신 CSS 로만 처리 — 매핑 없음 원칙 유지. */
    <div className="prose prose-sm prose-heviton max-w-none [&_table]:block [&_table]:overflow-x-auto">
      <ReactMarkdown
        remarkPlugins={indentedCode ? [remarkGfm] : [remarkGfm, remarkDisableIndentedCode]}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
